import React, { useEffect, useState } from 'react';
import { useGMapLoad } from '../5templates/googleMap/useGMapLoad';

import InputField from '../1elements/Fields/InputField';
import GenericMap from '../5templates/googleMap/genericMap';

const UniversalAddress = ({
  values,
  editMode,
  setFieldValue,
  addressIsInArray = true,
  allowUnit = true,
  allowCivic = true,
  allowManualLatLongOverride = false,
  setGeo = (geo) => {}
}) => {
  const isGoogleMapAPILoaded = useGMapLoad();
  const latLongEditMode = editMode && allowManualLatLongOverride;
  const [allowManualPostalCodeEdit, setAllowManualPostalCodeEdit] =
    useState(false);

  const readAddressFieldValue = (part) => {
    if (addressIsInArray) {
      if (values.addresses?.[0]) {
        return values.addresses[0][part] ?? '';
      } else {
        return '';
      }
    } else {
      return values.address?.[part] ? values.address[part] : '';
    }
  };

  const getAddressFieldNameForWriting = (part) => {
    if (addressIsInArray) {
      return `addresses.0.${part}`;
    } else {
      return `address.${part}`;
    }
  };

  useEffect(() => {
    if (!window.google) return;

    if (!window.google.maps) return;

    if (!isGoogleMapAPILoaded) return;

    if (!editMode) return;

    const addressMetaData = {
      street_number: 'short_name',
      route: 'long_name',
      locality: 'long_name',
      sublocality: 'long_name',
      sublocality_level_1: 'long_name',
      administrative_area_level_2: 'long_name',
      administrative_area_level_1: 'long_name',
      country: 'long_name',
      postal_code: 'short_name'
    };

    const addressData = {
      street_number: '',
      route: '',
      locality: '',
      sublocality: '',
      sublocality_level_1: '',
      administrative_area_level_1: '',
      administrative_area_level_2: '',
      country: '',
      postal_code: '',
      lat: 0,
      long: 0
    };

    const options = {
      // fields: ['geometry', 'name', 'address'],
      // strictBounds: false,
      types: ['address']
    };
    const triggerElement = document.getElementById('googleAutoCompleteBox');

    // preventing google selection from submitting the form
    window.google.maps.event.addDomListener(
      triggerElement,
      'keydown',
      function (e) {
        if (e.keyCode === 13) {
          if (e.preventDefault) {
            e.preventDefault();
          } else {
            // Since the google event handler framework does not handle
            e.cancelBubble = true;
            e.returnValue = false;
          }
        }
      }
    );
    const autoComplete = new window.google.maps.places.Autocomplete(
      triggerElement,
      options
    );

    autoComplete.addListener('place_changed', () => {
      const place = autoComplete.getPlace();
      console.log('RAW FROM GOOGLE', place);
      if (place.geometry) {
        addressData.lat = place.geometry.location.lat() ?? '';
        addressData.long = place.geometry.location.lng() ?? '';
      }
      if (place.address_components?.length) {
        for (var i = 0; i < place.address_components.length; i++) {
          var addressType = place.address_components[i].types[0];
          if (addressMetaData[addressType]) {
            const val =
              place.address_components[i][addressMetaData[addressType]];
            addressData[addressType] = val ?? '';
          } else {
            console.log(
              'logging google address part component that does not fit into common North American schema:'
            );
            console.log(
              `address type of ${addressType} has the value of ${place.address_components[i]['long_name']}`
            );
          }
        }
      }

      console.log('AFTER PARSING', addressData);
      setGeo({
        long: addressData.long,
        lat: addressData.lat
      });

      if (addressData.postal_code) {
        setAllowManualPostalCodeEdit(false);
      } else {
        setAllowManualPostalCodeEdit(true);
      }

      setFieldValue(
        getAddressFieldNameForWriting('street'),
        `${addressData.street_number} ${addressData.route}`.trim()
      );
      setFieldValue(
        getAddressFieldNameForWriting('city'),
        addressData.locality
          ? (addressData.locality ?? '')
          : (addressData.administrative_area_level_2 ?? '')
            ? (addressData.administrative_area_level_2 ?? '')
            : (addressData.sublocality ?? '')
              ? (addressData.sublocality ?? '')
              : (addressData.sublocality_level_1 ?? '')
      );
      setFieldValue(
        getAddressFieldNameForWriting('region'),
        addressData.administrative_area_level_1 ?? ''
      );
      setFieldValue(
        getAddressFieldNameForWriting('country'),
        addressData.country ?? ''
      );
      setFieldValue(
        getAddressFieldNameForWriting('postal'),
        addressData.postal_code ?? ''
      );
      setFieldValue(
        getAddressFieldNameForWriting('lat'),
        addressData.lat ?? ''
      );
      setFieldValue(
        getAddressFieldNameForWriting('long'),
        addressData.long ?? ''
      );
    });
    // eslint-disable-next-line
  }, [isGoogleMapAPILoaded, editMode]);

  return (
    <div>
      {/* without the map on here, google places api refuses to work, so the map is displayed with ZERO height */}
      <GenericMap />
      {editMode && allowCivic && (
        <input type="text" id="googleAutoCompleteBox"></input>
      )}
      {allowCivic && (
        <InputField
          type="text"
          name={getAddressFieldNameForWriting('street')}
          maxLength={50}
          label="Street"
          editMode={false}
          value={readAddressFieldValue('street') ?? ''}
        />
      )}

      {allowUnit && allowCivic && (
        <InputField
          type="text"
          name={getAddressFieldNameForWriting('unit')}
          maxLength={50}
          label="Unit"
          editMode={editMode}
          required={false}
          value={readAddressFieldValue('unit') ?? ''}
        />
      )}

      {allowCivic && (
        <InputField
          type="text"
          name={getAddressFieldNameForWriting('city')}
          maxLength={50}
          label="City"
          editMode={false}
          value={readAddressFieldValue('city') ?? ''}
        />
      )}

      {allowCivic && (
        <InputField
          type="text"
          name={getAddressFieldNameForWriting('region')}
          maxLength={50}
          label="State"
          editMode={false}
          value={readAddressFieldValue('region') ?? ''}
        />
      )}

      {allowCivic && (
        <InputField
          type="text"
          name={getAddressFieldNameForWriting('country')}
          maxLength={50}
          label="Country"
          editMode={false}
          value={readAddressFieldValue('country') ?? ''}
        />
      )}

      {allowCivic && (
        <InputField
          type="text"
          name={getAddressFieldNameForWriting('postal')}
          maxLength={50}
          label="Postal/Zip Code"
          required={true}
          editMode={editMode && allowManualPostalCodeEdit}
          value={readAddressFieldValue('postal') ?? ''}
        />
      )}

      <InputField
        type="text"
        name={getAddressFieldNameForWriting('lat')}
        maxLength={50}
        label="Latitude"
        editMode={latLongEditMode}
        value={readAddressFieldValue('lat') ?? ''}
      />
      <InputField
        type="text"
        name={getAddressFieldNameForWriting('long')}
        maxLength={50}
        label="Longitude"
        editMode={latLongEditMode}
        value={readAddressFieldValue('long') ?? ''}
      />
    </div>
  );
};

export default UniversalAddress;
